import React, { useState, useRef } from 'react';
import { Camera, QrCode, FileText, Upload, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

export function MobileScanner() {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [scanType, setScanType] = useState<'qr' | 'document'>('qr');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleStartScan = () => {
    setIsScanning(true);
    setScanResult(null);
    // In a real implementation, this would initialize the camera
    // For now, we'll simulate scanning
    setTimeout(() => {
      setScanResult('Sample QR Code Result');
      setIsScanning(false);
    }, 2000);
  };

  const handleStopScan = () => {
    setIsScanning(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleProcessDocument = () => {
    if (uploadedFile) {
      // Process the uploaded document
      console.log('Processing document:', uploadedFile.name);
      // In a real implementation, this would send the file to a processing service
    }
  };

  const handleClearResult = () => {
    setScanResult(null);
    setUploadedFile(null);
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Scanner</h2>
        <p className="text-sm text-gray-600">
          Scan QR codes or upload documents for processing
        </p>
      </div>

      {/* Scan Type Selector */}
      <div className="flex gap-2">
        <Button
          variant={scanType === 'qr' ? 'default' : 'outline'}
          onClick={() => setScanType('qr')}
          className="flex-1"
        >
          <QrCode size={16} className="mr-2" />
          QR Code
        </Button>
        <Button
          variant={scanType === 'document' ? 'default' : 'outline'}
          onClick={() => setScanType('document')}
          className="flex-1"
        >
          <FileText size={16} className="mr-2" />
          Document
        </Button>
      </div>

      {/* Scanner Interface */}
      {scanType === 'qr' && (
        <Card className="p-6">
          <div className="text-center space-y-4">
            {!isScanning && !scanResult && (
              <>
                <div className="w-32 h-32 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <QrCode size={48} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-600">
                  Position the QR code within the frame to scan
                </p>
                <Button onClick={handleStartScan} className="w-full">
                  <Camera size={16} className="mr-2" />
                  Start Scanning
                </Button>
              </>
            )}

            {isScanning && (
              <>
                <div className="w-32 h-32 mx-auto bg-green-100 rounded-lg flex items-center justify-center border-2 border-green-500">
                  <Camera size={48} className="text-green-500 animate-pulse" />
                </div>
                <p className="text-sm text-gray-600">Scanning...</p>
                <Button onClick={handleStopScan} variant="outline" className="w-full">
                  <X size={16} className="mr-2" />
                  Stop Scanning
                </Button>
              </>
            )}

            {scanResult && (
              <>
                <div className="w-32 h-32 mx-auto bg-green-100 rounded-lg flex items-center justify-center">
                  <Check size={48} className="text-green-500" />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Scan Result:</p>
                  <p className="text-sm text-gray-600 break-all">{scanResult}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleClearResult} variant="outline" className="flex-1">
                    Clear
                  </Button>
                  <Button className="flex-1">
                    Process Result
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Document Upload Interface */}
      {scanType === 'document' && (
        <Card className="p-6">
          <div className="text-center space-y-4">
            {!uploadedFile && (
              <>
                <div className="w-32 h-32 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <Upload size={48} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-600">
                  Upload a document to scan and process
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                >
                  <Upload size={16} className="mr-2" />
                  Choose File
                </Button>
              </>
            )}

            {uploadedFile && (
              <>
                <div className="w-32 h-32 mx-auto bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText size={48} className="text-green-500" />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Selected File:</p>
                  <p className="text-sm text-gray-600">{uploadedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleClearResult} variant="outline" className="flex-1">
                    Clear
                  </Button>
                  <Button onClick={handleProcessDocument} className="flex-1">
                    Process Document
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Recent Scans */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Recent Scans</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-2 rounded bg-gray-50">
            <QrCode size={16} className="text-blue-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">QR Code Scan</p>
              <p className="text-xs text-gray-500">2 minutes ago</p>
            </div>
            <span className="text-xs text-green-600">Processed</span>
          </div>
          <div className="flex items-center gap-3 p-2 rounded bg-gray-50">
            <FileText size={16} className="text-green-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Document Scan</p>
              <p className="text-xs text-gray-500">1 hour ago</p>
            </div>
            <span className="text-xs text-green-600">Processed</span>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Scanning Tips</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Ensure good lighting for better scan quality</li>
          <li>• Hold the device steady when scanning QR codes</li>
          <li>• Use high-resolution images for document scanning</li>
          <li>• Supported formats: PDF, JPG, PNG, DOC, DOCX</li>
        </ul>
      </div>
    </div>
  );
} 